/**
 * CSV Validation Utilities
 *
 * Zod schema and validation helpers for CSV event data.
 * Uses chrono-node for flexible date/time parsing.
 */

import { z } from 'zod';
import * as chrono from 'chrono-node';
import { EventStatus } from '../api/events';
import { localDayStartIso, zonedWallTimeToDate } from './dateFormatting';

// =============================================================================
// Date/Time Parsing Utilities
// =============================================================================

/**
 * Parse a date string in various formats and return YYYY-MM-DD
 *
 * Supported formats:
 * - ISO: 2026-01-21, 2026/01/21
 * - US: 01/21/2026, 1/21/2026, 01-21-2026
 * - Written: January 21, 2026, Jan 21, 2026
 * - With day: Wednesday, January 21, 2026
 */
export function parseFlexibleDate(input: string): { success: true; value: string } | { success: false; error: string } {
  if (!input || input.trim() === '') {
    return { success: false, error: 'Date is required' };
  }

  const trimmed = input.trim();

  // Use chrono to parse the date
  const parsed = chrono.parseDate(trimmed);

  if (!parsed) {
    return {
      success: false,
      error: `Could not parse date "${trimmed}". Try formats like: 2026-01-21, 01/21/2026, January 21, 2026`
    };
  }

  // Format as YYYY-MM-DD
  const year = parsed.getFullYear();
  const month = String(parsed.getMonth() + 1).padStart(2, '0');
  const day = String(parsed.getDate()).padStart(2, '0');

  return { success: true, value: `${year}-${month}-${day}` };
}

/**
 * Parse a time string in various formats and return HH:MM (24-hour)
 *
 * Supported formats:
 * - 24-hour: 16:00, 9:00, 09:00
 * - 12-hour: 4:00 PM, 4:00PM, 4PM, 4:00 pm
 */
export function parseFlexibleTime(input: string): { success: true; value: string } | { success: false; error: string } {
  if (!input || input.trim() === '') {
    return { success: true, value: '' }; // Times are optional
  }

  const trimmed = input.trim();

  // Use a reference date for time-only parsing
  const refDate = new Date('2026-01-01T00:00:00');
  const parsed = chrono.parseDate(trimmed, refDate, { forwardDate: true });

  if (!parsed) {
    return {
      success: false,
      error: `Could not parse time "${trimmed}". Try formats like: 16:00, 4:00 PM, 4PM`
    };
  }

  // Format as HH:MM
  const hours = String(parsed.getHours()).padStart(2, '0');
  const minutes = String(parsed.getMinutes()).padStart(2, '0');

  return { success: true, value: `${hours}:${minutes}` };
}

// =============================================================================
// Zod Schema with Flexible Parsing
// =============================================================================

/**
 * Custom Zod refinement for flexible date parsing
 */
const flexibleDateSchema = z
  .string()
  .min(1, 'Event date is required')
  .transform((val, ctx) => {
    const result = parseFlexibleDate(val);
    if (!result.success) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: result.error,
      });
      return z.NEVER;
    }
    return result.value;
  });

/**
 * Custom Zod refinement for flexible time parsing
 */
const flexibleTimeSchema = z
  .string()
  .optional()
  .transform((val, ctx) => {
    if (!val || val.trim() === '') return undefined;
    const result = parseFlexibleTime(val);
    if (!result.success) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: result.error,
      });
      return z.NEVER;
    }
    return result.value || undefined;
  });

/**
 * Schema for a single CSV row (raw string values from CSV)
 * Dates and times are flexibly parsed and normalized
 */
export const csvRowSchema = z.object({
  name: z
    .string()
    .min(1, 'Event name is required')
    .max(200, 'Event name must be less than 200 characters'),
  venue: z
    .string()
    .min(1, 'Venue is required')
    .max(200, 'Venue must be less than 200 characters'),
  eventDate: flexibleDateSchema,
  callTime: flexibleTimeSchema,
  startTime: flexibleTimeSchema,
  endTime: flexibleTimeSchema,
  network: z.string().max(100, 'Network must be less than 100 characters').optional(),
  sport: z.string().max(100, 'Sport must be less than 100 characters').optional(),
  eventType: z.string().max(100, 'Event type must be less than 100 characters').optional(),
  status: z
    .enum(['DRAFT', 'PUBLISHED', 'COMPLETED', 'CANCELLED'])
    .optional()
    .default('DRAFT'),
});

export type CSVRowInput = z.input<typeof csvRowSchema>;
export type CSVRowOutput = z.output<typeof csvRowSchema>;

/**
 * Validation result for a single row
 */
export interface RowValidationResult {
  rowIndex: number;
  isValid: boolean;
  /** Original input data (for display in preview) */
  inputData: CSVRowInput;
  /** Normalized/parsed data (for API submission) - only set when isValid is true */
  parsedData: CSVRowOutput | null;
  errors: string[];
}

/**
 * Validate a single CSV row
 */
export function validateRow(row: CSVRowInput, rowIndex: number): RowValidationResult {
  const result = csvRowSchema.safeParse(row);

  if (result.success) {
    return {
      rowIndex,
      isValid: true,
      inputData: row,
      parsedData: result.data,
      errors: [],
    };
  }

  const errors = result.error.errors.map((err) => {
    const path = err.path.join('.');
    return path ? `${path}: ${err.message}` : err.message;
  });

  return {
    rowIndex,
    isValid: false,
    inputData: row,
    parsedData: null,
    errors,
  };
}

/**
 * Validate all CSV rows
 */
export function validateRows(rows: CSVRowInput[]): RowValidationResult[] {
  return rows.map((row, index) => validateRow(row, index));
}

/**
 * Convert a validated CSV row to API format (ISO datetime strings)
 */
export function convertRowToAPIFormat(row: CSVRowOutput): {
  name: string;
  venue: string;
  network: string | null;
  eventDate: string;
  callTime: string | null;
  startTime: string | null;
  endTime: string | null;
  sport: string | null;
  eventType: string | null;
  status: EventStatus;
} {
  // Convert date and times to ISO format: the CSV's wall-clock date and times
  // are in the org's zone, same as EventForm.
  const eventDate = localDayStartIso(row.eventDate);

  const convertTime = (date: string, time: string | undefined): string | null => {
    if (!time || time.trim() === '') return null;
    const [hours, minutes] = time.split(':').map(Number);
    return zonedWallTimeToDate(date, hours, minutes).toISOString();
  };

  return {
    name: row.name,
    venue: row.venue,
    network: row.network || null,
    eventDate,
    callTime: convertTime(row.eventDate, row.callTime),
    startTime: convertTime(row.eventDate, row.startTime),
    endTime: convertTime(row.eventDate, row.endTime),
    sport: row.sport || null,
    eventType: row.eventType || null,
    status: row.status || 'DRAFT',
  };
}

/**
 * Convert multiple validated rows to API format
 */
export function convertRowsToAPIFormat(rows: CSVRowOutput[]) {
  return rows.map(convertRowToAPIFormat);
}

/**
 * Get the list of valid CSV column names
 */
export const CSV_COLUMNS = [
  'name',
  'venue',
  'network',
  'eventDate',
  'callTime',
  'startTime',
  'endTime',
  'sport',
  'eventType',
  'status',
] as const;

/**
 * Required columns
 */
export const REQUIRED_COLUMNS = ['name', 'venue', 'eventDate'] as const;
